import React, { useState, useEffect, useCallback } from 'react';
import { Linking, View, Text, TouchableOpacity, Image, ScrollView, StyleSheet, Dimensions, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { fetchAuthSession } from 'aws-amplify/auth';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from './App.web';
import { apiUrl } from './utils/config';
import Purchases, { PurchasesPackage } from 'react-native-purchases';
import { useFocusEffect } from '@react-navigation/native';
import { loadStripe } from '@stripe/stripe-js';
import * as Font from 'expo-font';
import ParentalGateModal from './ParentalGateModal';



const { width: screenWidth } = Dimensions.get('window'); // Get screen width

type NavigationProp = StackNavigationProp<RootStackParamList, 'SubscriptionPage'>;

type PlanType = 'Premium' | 'Standard' | 'Basic';

interface TokensOption {
  tokens: number;
  price: number;
  label?: string;
}

const monthlyPrices: Record<PlanType, number> = {
  Premium: 32.00,
  Standard: 16.40,
  Basic: 8.50,
};

const annualPrices: Record<PlanType, string> = {
  Premium: (monthlyPrices.Premium * 12).toFixed(2),
  Standard: (monthlyPrices.Standard * 12).toFixed(2),
  Basic: (monthlyPrices.Basic * 12).toFixed(2),
};


const SubscriptionPage: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null);
  const [billingCycle, setBillingCycle] = useState('Monthly');
  const [activeSection, setActiveSection] = useState('Monthly');
  const [userTokens, setUserTokens] = useState(0);
  const [selectedTokensOption, setSelectedTokensOption] = useState<TokensOption | null>(null);
  const [products, setProducts] = useState<PurchasesPackage[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentPlan, setCurrentPlan] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState<PurchasesPackage[]>([]);
  const [subscriptionInfo, setSubscriptionInfo] = useState<{
    store?: string;
    product_id?: string;
    transaction_id?: string;
  } | null>(null);
  const [nativeProducts, setNativeProducts] = useState<PurchasesPackage[]>([]);
  const [webProducts, setWebProducts] = useState<
    { identifier: string; platformProductIdentifier: string; price: number; currency: string }[]
  >([]);
  const PDF_URL = "https://legal.interactivebedtime.com/terms.html";
  const [showParentalGate, setShowParentalGate] = useState(false);
  const [parentalGateIntent, setParentalGateIntent] = useState<'purchase' | 'cancel' | 'terms' | 'privacy' | null>(null);
  const [parentalGatePassed, setParentalGatePassed] = useState(Platform.OS === 'android');
  const isContinueDisabled = !selectedPlan && !selectedTokensOption
  const [userId, setUserId] = useState<string | null>(null);


  const androidMappings: Record<string, number> = {
    'token_1': 1,
    'token_5': 5,
    'token_20': 20,
    'sub_basic:sub-basic-monthly': 4,
    'basic_monthly': 4,
    'sub_standard:sub-standard-monthly': 8,
    'standard_monthly': 8,
    'sub_premium:sub-premium-monthly': 16,
    'premium_monthly': 16,
    'sub_basic:sub-basic-annual': 4,
    'basic_annual': 4,
    'sub_standard:sub-standard-annual': 8,
    'standard_annual': 8,
    'sub_premium:sub-premium-annual': 16,
    'premium_annual': 16,
  };

  const extraTokenOptions: TokensOption[] = (Platform.OS === 'web'
    ? webProducts
    : nativeProducts
  )
    .filter((p: any) => {
      const id = Platform.OS === 'web' ? p.identifier : p.product.identifier;
      return id.startsWith('token_') || id.endsWith('_token');
    })
    .map((p: any) => {
      const id = Platform.OS === 'web' ? p.identifier : p.product.identifier;
      const price = Platform.OS === 'web' ? p.price : p.product.price;
      const tokens = parseInt(id.match(/\d+/)?.[0] || '0', 10);
  
      return {
        tokens,
        price,
        label:
          tokens === 20
            ? 'Best Value'
            : tokens === 5
            ? 'Most Popular'
            : undefined,
      };
    });
  
  
  

    const initializeRevenueCat = async () => {
    try {
      const user_Id = await getUserID();
      setUserId(user_Id);
      const apiKey = await getRevenueCatApiKey();
  
      if (Platform.OS === 'web') {
        console.log('Initializing RevenueCat for web with Stripe');
        // For web, no SDK initialization is required
        return;
      }
  
      console.log(`Initializing RevenueCat with user ID: ${user_Id}`);
      await Purchases.configure({
        apiKey,
        appUserID: user_Id,
      });
    } catch (error) {
      console.error('Failed to initialize RevenueCat:', error);
    }
  };
  

  const getRevenueCatApiKey = () => {
    switch (Platform.OS) {
      case 'web':
        return 'strp_XASGSIyxoqfMtytKArPtAlUBdMJ';
      case 'android':
        return 'goog_iLAzEACHHDPDRMajYMBCIazNbKn';
      case 'ios':
        return 'appl_eCgxRicfFfVYwZSropObLoLEfVh';
      default:
        throw new Error(`Unsupported platform: ${Platform.OS}`);
    }
  };

  const fetchProducts = useCallback(async () => {
    try {
      if (Platform.OS === 'web') {
        const userId = await getUserID();
        const offeringsResponse = await fetch(`https://api.revenuecat.com/v1/subscribers/${userId}/offerings`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${getRevenueCatApiKey()}`,
            'X-Platform': 'stripe',
          },
        });
  
        if (!offeringsResponse.ok) throw new Error(`Failed to fetch offerings: ${offeringsResponse.statusText}`);
  
        const offeringsData = await offeringsResponse.json();
        const flatPackages = offeringsData.offerings.flatMap((offering: any) =>
          offering.packages.map((pkg: any) => ({
            identifier: pkg.identifier,
            platformProductIdentifier: pkg.platform_product_identifier,
          }))
        );
  
        const productIds = flatPackages.map((p: { platformProductIdentifier: any; }) => p.platformProductIdentifier);
  
        const query = `product_ids=${productIds.join(',')}`;
        const priceResponse = await fetch(`${apiUrl}tokens/get-prices?${query}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });


  
        if (!priceResponse.ok) throw new Error('Failed to fetch Stripe prices');
  
        const priceMap = await priceResponse.json();
  
        const enrichedProducts = flatPackages.map((pkg: { platformProductIdentifier: string | number; }) => ({
          ...pkg,
          price: priceMap[pkg.platformProductIdentifier]?.price || 0,
          currency: priceMap[pkg.platformProductIdentifier]?.currency || 'USD',
        }));
  
        setProducts(enrichedProducts);
        setWebProducts(enrichedProducts);
      } else {
        const offerings = await Purchases.getOfferings();
        console.log(offerings);
        if (offerings.current) {
          setProducts(offerings.current.availablePackages);
          setNativeProducts(offerings.current.availablePackages);
        } else {
          setProducts([]);
          setNativeProducts([]);
        }
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, []);

  const loadFonts = async () => {
        await Font.loadAsync({
          'Futura': require('./assets/fonts/futura.ttf'),
        });
      };
  

  useEffect(() => {
    const initializeAndFetch = async () => {
      await initializeRevenueCat(); 
      await fetchProducts();
      loadFonts();
    };
    initializeAndFetch();
  }, []);

  useFocusEffect(
      useCallback(() => {
        fetchUserTokens();
      }, [])
    );
  

  // Map product identifiers to token amounts
  const mapProductToTokens = (productId: string) => {
    const iOSMappings: Record<string, number> = {
      '1_token': 1,
      '5_token': 5,
      '20_token': 20,
      'basic_monthly_sub': 4,
      'standard_monthly_sub': 8,
      'premium_monthly_sub': 16,
      'basic_annual_sub': 48,
      'standard_annual_sub': 96,
      'premium_annual_sub': 192,
    };
  
    const androidMappings: Record<string, number> = {
      'token_1': 1,
      'token_5': 5,
      'token_20': 20,
      'sub_basic:sub-basic-monthly': 4,
      'sub_standard:sub-standard-monthly': 8,
      'sub_premium:sub-premium-monthly': 16,
      'sub_basic:sub-basic-annual': 4,
      'sub_standard:sub-standard-annual': 8,
      'sub_premium:sub-premium-annual': 16,
    };
  
    const platformMappings = Platform.OS === 'ios' ? iOSMappings : androidMappings;
    return platformMappings[productId] || 0;
  };
  
  // Backend API call
  const updateTokensInBackend = async (tokenChange: number) => {
    try {
      const user_id = await getUserID();
      const authToken = await getAuthToken();

      const response = await fetch(`${apiUrl}tokens/update-tokens`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          user_id,
          token_change: tokenChange,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Tokens successfully updated:', data);
    } catch (error) {
      console.error('Error updating tokens in backend:', error);
    }
  };

  const pollForUpdatedTokens = async (initialTokens: number) => {
    const interval = setInterval(async () => {
      try {
        const user_id = await getUserID();
        const authToken = await getAuthToken();
        const response = await fetch(`${apiUrl}settings/get-user-settings?user_id=${user_id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
        });
  
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
  
        const data = await response.json();
        const newTokenBalance = data.token_balance || 0;
  
        if (newTokenBalance !== initialTokens) {
          clearInterval(interval); // Stop polling once the token balance updates
          setUserTokens(newTokenBalance); // Update the state
          console.log('Token balance updated, stopping polling');
        }
      } catch (error) {
        console.error('Error fetching user tokens:', error);
        clearInterval(interval);
      }
    }, 3000); // Poll every 3 seconds
  };

  const handlePurchase = async (pkg: PurchasesPackage) => {
    if (Platform.OS === 'web') {
      if (subscriptionInfo && activeSection !== 'Extra Tokens') {
        setShowSubscriptionPopup(true);
      } else {
        // On mobile, proceed with the existing purchase flow
        console.log('purchase web')
        await handlePurchaseWeb(pkg);
      }
    } else if (Platform.OS === 'android') {
      try {
        const customerInfo = await Purchases.getCustomerInfo();
        const activeEntitlement = customerInfo.activeSubscriptions[0]; // Assuming single active subscription
        const initialTokens = userTokens;

        // Check if it's a one-time purchase (token purchase)
        const isOneTimePurchase = pkg.identifier.startsWith("token_");

        if (isOneTimePurchase) {
          // Simple in-app purchase flow (no subscription logic)
          const purchase = await Purchases.purchasePackage(pkg);
          if (purchase) {
            console.log('Token purchase successful:', purchase);
            const tokenChange = androidMappings[pkg.identifier] || 0;
            await updateTokensInBackend(tokenChange);
            pollForUpdatedTokens(initialTokens);
            setSelectedPlan(null);
            setSelectedTokensOption(null);
            fetchUserTokens(); // Refresh token balance
          }
          return;
        }
  
        let purchaseOptions: { oldSKU: string; prorationMode: number } | undefined;
  
        if (activeEntitlement && activeEntitlement !== pkg.identifier) {
          // Fetch token counts from the mapping
          const oldTokens = androidMappings[activeEntitlement] || 0;
          const newTokens = androidMappings[pkg.identifier] || 0
  
          // Determine if upgrade, downgrade, or same plan
          let prorationMode: number;
  
          if (newTokens > oldTokens) {
            // Upgrade: Immediate and charge prorated price
            prorationMode = Purchases.PRORATION_MODE.IMMEDIATE_AND_CHARGE_PRORATED_PRICE;
          } else {
            // Downgrade or Same Plan: Deferred proration
            prorationMode = Purchases.PRORATION_MODE.IMMEDIATE_WITHOUT_PRORATION;
          }
  
          purchaseOptions = {
            oldSKU: activeEntitlement,
            prorationMode: prorationMode,
          };
        }
  
        // Proceed with purchase
        const purchase = purchaseOptions
          ? await Purchases.purchasePackage(pkg, purchaseOptions)
          : await Purchases.purchasePackage(pkg);
  
        if (purchase) {
          console.log('Purchase successful:', purchase);
  
          // Determine token amount based on product identifier
          const tokenChange = androidMappings[pkg.identifier] || 0;
  
          // Call backend to update token balance
          await updateTokensInBackend(tokenChange);
  
          pollForUpdatedTokens(initialTokens);
  
          // Reset selections after purchase
          setSelectedPlan(null);
          setSelectedTokensOption(null);
          fetchUserTokens(); // Refresh tokens
        }
      } catch (error) {
        if (error instanceof Error && 'userCancelled' in error) {
          const typedError = error as { userCancelled: boolean };
          if (typedError.userCancelled) {
            console.log('User canceled the purchase');
            setSelectedPlan(null);
            setSelectedTokensOption(null);
            fetchProducts(); // Reset flow
          } else {
            console.error('Purchase failed:', error);
          }
        } else {
          console.error('An unknown error occurred:', error);
        }
      }
    } else if (Platform.OS === 'ios') {
      try {
        const initialTokens = userTokens;
  
        const purchase = await Purchases.purchasePackage(pkg); // iOS handles proration natively
  
        if (purchase) {
          console.log('Purchase successful:', purchase);
  
          const tokenChange = androidMappings[pkg.identifier] || 0;
          await updateTokensInBackend(tokenChange);
  
          pollForUpdatedTokens(initialTokens);
  
          setSelectedPlan(null);
          setSelectedTokensOption(null);
          fetchUserTokens();
        }
      } catch (error) {
        if (error instanceof Error && 'userCancelled' in error) {
          const typedError = error as { userCancelled: boolean };
          if (typedError.userCancelled) {
            console.log('User canceled the purchase');
            setSelectedPlan(null);
            setSelectedTokensOption(null);
            fetchProducts();
          } else {
            console.error('Purchase failed:', error);
          }
        } else {
          console.error('An unknown error occurred:', error);
        }
      }
    } else {
      console.warn(`Platform ${Platform.OS} is not supported for purchases.`);
    }
  };
  
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  
  const handlePurchaseWeb = async (pkg: any) => {
    try {
      const stripe = await loadStripe("pk_live_51QiiCAAlNTU1va9JoUZQoAwYiPEijmSpV4lCxsom8u5um8H3xF0HtdNURFuw4LAn6pp5gfdNyyyfa5ZXNzoZaaga00VFoy4A2S");
  
      // Call Lambda function via API Gateway
      const response = await fetch(`${apiUrl}tokens/create-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productId: pkg.platformProductIdentifier,
          appUserID: await getUserID(),
        }),
      });
  
      if (!response.ok) {
        throw new Error(`Failed to create checkout session: ${response.statusText}`);
      }
  
      const { sessionId } = await response.json();
  
      // Redirect to Stripe Checkout
      const result = await stripe?.redirectToCheckout({ sessionId });
  
      if (result?.error) {
        console.error("Stripe checkout error:", result.error.message);
      }
    } catch (error) {
      console.error("Error during web purchase:", error);
    }
  };
  
  const getPrice = (planOrToken: PlanType | string): string => {
    const lower = planOrToken.toLowerCase();
  
    const fallbackPrices: Record<'Monthly' | 'Annual', Record<PlanType, number>> = {
      Monthly: {
        Basic: 5.99,
        Standard: 10.99,
        Premium: 20.99,
      },
      Annual: {
        Basic: 64.99,
        Standard: 124.99,
        Premium: 249.99,
      },
    };
  
    const fallbackTokenPrices: Record<number, number> = {
      1: 26.99,
      5: 7.69,
      20: 1.59,
    };
  
    const tokenMatch = lower.match(/\d+/);
    const tokenAmount = tokenMatch ? parseInt(tokenMatch[0], 10) : null;
  
    if (Platform.OS === 'web') {
      const id = `${lower}_${billingCycle.toLowerCase()}`;
      const tokenId = `token_${lower}`;
      const product = webProducts.find(p =>
        p.identifier === id || p.identifier === tokenId
      );
  
      if (product?.price && product.price > 0) {
        return product.price.toFixed(2);
      }
  
      if (tokenAmount && fallbackTokenPrices[tokenAmount]) {
        return fallbackTokenPrices[tokenAmount].toFixed(2);
      }
  
      if (Object.keys(fallbackPrices[billingCycle as 'Monthly' | 'Annual']).includes(planOrToken)) {
        return fallbackPrices[billingCycle as 'Monthly' | 'Annual'][planOrToken as PlanType].toFixed(2);
      }
  
      return '0.00';
    } else {
      const id1 = `${lower}_${billingCycle.toLowerCase()}_sub`;
      const id2 = `sub_${lower}:sub-${lower}-${billingCycle.toLowerCase()}`;
      const tokenId = `token_${lower}`;
      const product = nativeProducts.find(p =>
        p.product.identifier === id1 ||
        p.product.identifier === id2 ||
        p.product.identifier === tokenId
      );
  
      if (product?.product?.price && product.product.price > 0) {
        return product.product.price.toFixed(2);
      }
  
      if (tokenAmount && fallbackTokenPrices[tokenAmount]) {
        return fallbackTokenPrices[tokenAmount].toFixed(2);
      }
  
      if (Object.keys(fallbackPrices[billingCycle as 'Monthly' | 'Annual']).includes(planOrToken)) {
        return fallbackPrices[billingCycle as 'Monthly' | 'Annual'][planOrToken as PlanType].toFixed(2);
      }
  
      return '0.00';
    }
  };
  
  
  

  const handleToggleSection = (section: string) => {
    setActiveSection(section);
    setSelectedPlan(null);
    setSelectedTokensOption(null);
  
    // Ensure `products` is an array
    if (!Array.isArray(products)) {
      console.warn('Products is not an array:', products);
      return;
    }
  
    let subset;
  
    if (Platform.OS === 'web') {
      // Web: Use `platformProductIdentifier` from web response
      if (section === 'Monthly' || section === 'Annual') {
        setBillingCycle(section);
  
        subset = products.filter((product: any) =>
          product.identifier?.toLowerCase().includes(section.toLowerCase())
        );
      } else if (section === 'Extra Tokens') {
        subset = products.filter((product: any) =>
          product.identifier?.toLowerCase().startsWith('token_')
        );
      }
    } else {
      // Native: Use `PurchasesPackage` structure
      if (section === 'Monthly' || section === 'Annual') {
        setBillingCycle(section);
  
        subset = products.filter((product: PurchasesPackage) =>
          product.product.identifier.toLowerCase().includes(section.toLowerCase())
        );
      } else if (section === 'Extra Tokens') {
        subset = products.filter((product: PurchasesPackage) =>
          product.product.identifier.toLowerCase().startsWith('token_')
        );
      }
    }
  
    setFilteredProducts(subset || []);
  };  
  

  const getAuthToken = useCallback(async (): Promise<any | null> => {
      try {
        const session = await fetchAuthSession();
        const idToken = session?.tokens?.idToken;
        const groups = session?.tokens?.idToken?.payload?.['cognito:groups'];
        if (Array.isArray(groups)) setIsAdmin(groups.includes('Admin'));
        return idToken;
      } catch (error) {
        console.error('Error retrieving auth token:', error);
        return null;
      }
    }, []);

  const getUserID = async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      const user_id = session?.tokens?.accessToken?.payload?.sub;
      return user_id || null;
    } catch (error) {
      console.error('Error retrieving user ID:', error);
      return null;
    }
  };
  

  useEffect(() => {
    getAuthToken();
  });

  const fetchUserTokens = useCallback(async () => {
    try {
      const user_id = await getUserID();
      if (!user_id) throw new Error('Unable to retrieve user ID');
  
      const authToken = await getAuthToken();
      if (!authToken) throw new Error('Unable to retrieve auth token');
  
      const response = await fetch(`${apiUrl}settings/get-user-settings?user_id=${user_id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const data = await response.json();
      setUserTokens(data.token_balance || 0);
  
      // ✅ Store subscription info for cancellation
      if (data.subscription_info) {
        setSubscriptionInfo(data.subscription_info);
      }
  
      if (data.subscription_level && data.subscription_type) {
        const subLevel =
          data.subscription_level.charAt(0).toUpperCase() + data.subscription_level.slice(1);
        const subType =
          data.subscription_type.charAt(0).toUpperCase() + data.subscription_type.slice(1);
        setCurrentPlan(subLevel + subType);
  
        handleToggleSection(subType);
      }
    } catch (error) {
      console.error('Error fetching user tokens:', error);
    }
  }, [apiUrl, getAuthToken]);
  
  const handleSelectOption = (option: TokensOption) => {
    setSelectedTokensOption(option);
    setSelectedPlan(null);
  };

  const handleSelectPlan = (plan: PlanType) => {
    setSelectedPlan(plan);
    setSelectedTokensOption(null);
  };

  const handleContinueToPurchase = () => {
    let selectedProduct;
  
    if (activeSection === 'Extra Tokens' && selectedTokensOption) {
      // Web: Use `identifier`
      if (Platform.OS === 'web') {
        selectedProduct = products.find(
          (product) => product.identifier === `token_${selectedTokensOption.tokens}`
        );
      } else if (Platform.OS === 'ios') {
        // Native: Use `product.identifier`
        selectedProduct = products.find(
          (product: PurchasesPackage) =>
            product.product.identifier === `${selectedTokensOption.tokens}_token`
        );
      } else {
        // Native: Use `product.identifier`
        selectedProduct = products.find(
          (product: PurchasesPackage) =>
            product.product.identifier === `token_${selectedTokensOption.tokens}`
        );
      }
  
      if (selectedProduct) {
        setSelectedPlan(null); // Reset plan selection
        handlePurchase(selectedProduct);
      }
    } else if (selectedPlan) {
      // Web: Use `identifier`
      if (Platform.OS === 'web') {
        selectedProduct = products.find(
          (product) =>
            product.identifier ===
            `${selectedPlan.toLowerCase()}_${billingCycle.toLowerCase()}`
        );
      } else if (Platform.OS === 'ios') {
        // Native: Use `product.identifier`
        selectedProduct = products.find(
          (product: PurchasesPackage) =>
            product.product.identifier ===
            `${selectedPlan.toLowerCase()}_${billingCycle.toLowerCase()}_sub`
        );
        console.log(selectedProduct)
      } else {
        // Native: Use `product.identifier`
        selectedProduct = products.find(
          (product: PurchasesPackage) =>
            product.product.identifier ===
            `sub_${selectedPlan.toLowerCase()}:sub-${selectedPlan.toLowerCase()}-${billingCycle.toLowerCase()}`
        );
        console.log(selectedProduct)
      }
      
      if (selectedProduct) {
        setSelectedTokensOption(null); // Reset token selection
        console.log(selectedProduct)
        handlePurchase(selectedProduct);
      }
    }
  };    

  const handleBack = () => {
    navigation.navigate('StoriesPage');
  };

  // Example function to handle showing the modal
  function handleShowCancelModal() {
    setShowCancelModal(true);
  }
  
  
  /**
   * Cancel a Stripe subscription by calling your dev backend,
   * which in turn handles the Stripe REST API logic.
   */
  async function cancelStripeSubscriptionOnServer(
    subscriptionId: string, // The Stripe subscription id, e.g. "sub_abc123"
    immediateCancel: boolean = false
  ) {
    const userId = await getUserID()
    const response = await fetch(`${apiUrl}tokens/cancel-subscription`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: userId,
        store_type: "STRIPE",
        subscription_id: subscriptionId, // "sub_abc123"
        immediate_cancel: immediateCancel,
        subscriptionInfo
      }),
    });
  
    if (!response.ok) {
      throw new Error(`Failed to cancel Stripe subscription. HTTP ${response.status}`);
    }
  
    console.log("Stripe subscription canceled via dev endpoint");
  }
  
  async function openSubscriptionsManually(store: 'ios' | 'android') {
    try {
      const url = store === 'ios'
        ? 'https://apps.apple.com/account/subscriptions'
        : 'https://play.google.com/store/account/subscriptions';
  
      await Linking.openURL(url);
      console.log(`Opened ${store === 'ios' ? 'App Store' : 'Google Play'} subscription management URL.`);
    } catch (error) {
      console.error('Failed to open subscription management page:', error);
    }
  }
  
  
  async function handleConfirmCancel() {
    setShowCancelModal(false);
    try {
      const userId = await getUserID();
      
      if (!subscriptionInfo) {
        throw new Error('❌ No subscription info available for cancellation.');
      }
  
      const { store, product_id, transaction_id } = subscriptionInfo;
  
      if (!store || !product_id || !transaction_id) {
        throw new Error('❌ Subscription data is incomplete.');
      }
  
      console.log(`🔹 Canceling ${store} subscription:`, { product_id, transaction_id });
  
      if (store === 'APP_STORE') {
        await openSubscriptionsManually('ios');
      } else if (store === 'PLAY_STORE') {
        await openSubscriptionsManually('android');
      } else if (store === 'STRIPE') {
        await cancelStripeSubscriptionOnServer(transaction_id);
      } else {
        console.warn(`Unsupported store type: ${store}`);
      }
    } catch (error) {
      console.error('❌ Error canceling subscription:', error);
    }
  } 
  
  const handleParentalGateSuccess = () => {
    setShowParentalGate(false);
    setParentalGatePassed(true);

    switch (parentalGateIntent) {
      case 'purchase':
        handleContinueToPurchase();
        break;
      case 'cancel':
        handleShowCancelModal();
        break;
      case 'terms':
        Linking.openURL(PDF_URL);
        break;
      case 'privacy':
        Linking.openURL("https://legal.interactivebedtime.com/privacy_policy.html");
        break;
      default:
        break;
    }

    setParentalGateIntent(null);
  };

  
  const handleParentalGateCancel = () => {
    setShowParentalGate(false);
    setParentalGateIntent(null);
  };
  
  // Then in your render (for the button):
  const userAlreadyHasThisPlan = selectedPlan && (
    currentPlan.toLowerCase().includes(selectedPlan.toLowerCase()) &&
    currentPlan.toLowerCase().includes(billingCycle.toLowerCase())
  );

  return (
    <View style={styles.subscriptionPage}>
    <View style={styles.header}>
        <TouchableOpacity onPress={() => handleBack()} style={styles.backButton}>
          <Image source={require('./assets/back-btn.png')} style={styles.backIcon} />
        </TouchableOpacity>

        <TouchableOpacity style={styles.tokensButton}>
          <Image source={require('./assets/tokens.png')} style={styles.tokenIcon} />
          <Text style={styles.tokenCount}>{userTokens}</Text>
        </TouchableOpacity>

        {isAdmin && (
          <TouchableOpacity
            style={styles.adminButton}
            onPress={() => navigation.navigate('AdminUserManagementPage')}
          >
            <Text style={styles.adminButtonText}>Manage Users</Text>
          </TouchableOpacity>
        )}
      </View>
    <ScrollView 
      contentContainerStyle={styles.container}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
    >
        <Text style={styles.title}>Choose Your Plan</Text>

        <View style={styles.subscriptionToggle}>
          <TouchableOpacity
            style={[styles.toggleButton, activeSection === 'Monthly' && styles.activeButton]}
            onPress={() => handleToggleSection('Monthly')}
          >
            <Text style={[styles.toggleButtonText, activeSection === 'Monthly' && styles.activeButtonText]}>
              Monthly
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.toggleButton, activeSection === 'Annual' && styles.activeButton]}
            onPress={() => handleToggleSection('Annual')}
          >
            <Text style={[styles.toggleButtonText, activeSection === 'Annual' && styles.activeButtonText]}>
              Annual
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.toggleButton, activeSection === 'Extra Tokens' && styles.activeButton]}
            onPress={() => handleToggleSection('Extra Tokens')}
          >
            <Text style={[styles.toggleButtonText, activeSection === 'Extra Tokens' && styles.activeButtonText]}>
              Extra Tokens
            </Text>
          </TouchableOpacity>
        </View>

        <View style={styles.subscriptionCards}>
        {activeSection !== 'Extra Tokens' ? (
          (Object.keys(monthlyPrices) as PlanType[]).map((plan) => {
            // Define a quick check comparing "plan" + "billingCycle" to "currentPlan"
            const isUserPlan =
              currentPlan.toLowerCase().includes(plan.toLowerCase()) &&
              currentPlan.toLowerCase().includes(billingCycle.toLowerCase());

            return (
              <TouchableOpacity
                key={plan}
                style={[
                  styles.subscriptionCard,
                  selectedPlan === plan && styles.activeCard,
                  // GOLD BACKGROUND IF IT’S USER’S CURRENT PLAN
                  isUserPlan && { backgroundColor: '#afc9be' }
                ]}
                onPress={() => handleSelectPlan(plan)}
              >
                <View style={styles.cardContent}>
                  <View style={styles.leftContent}>
                    <View style={styles.titleRow}>
                      <Text style={[styles.planTitle, selectedPlan === plan && styles.selectedText]}>
                        {plan}
                      </Text>
                      {plan === 'Premium' && (
                        <Text style={[styles.badge, selectedPlan === plan && styles.activeBadge]}>
                          Best Value
                        </Text>
                      )}
                      {plan === 'Standard' && (
                        <Text style={[styles.badge, selectedPlan === plan && styles.activeBadge]}>
                          Most Popular
                        </Text>
                      )}
                    </View>
                    <Text style={[styles.subscriptionBenefit, selectedPlan === plan && styles.selectedText]}>
                      + Save {plan === 'Premium' ? '60%' : plan === 'Standard' ? '50%' : '20%'}
                    </Text>
                    <Text style={[styles.subscriptionTokens, selectedPlan === plan && styles.selectedText]}>
                      + {plan === 'Premium' ? '16' : plan === 'Standard' ? '8' : '4'} tokens/month
                    </Text>
                  </View>
                  <View style={styles.priceContainer}>
                    <Text style={[styles.subscriptionPrice, selectedPlan === plan && styles.selectedText]}>
                      ${getPrice(plan)}
                    </Text>
                    <Text style={[styles.billingPeriod, selectedPlan === plan && styles.selectedText]}>
                      {billingCycle}
                    </Text>
                  </View>
                </View>
              </TouchableOpacity>
            );
          })
        ) : (
          <View style={styles.extraTokensSection}>
            {extraTokenOptions.map((option) => (
              <TouchableOpacity
                key={option.tokens}
                style={[
                  styles.subscriptionCard,
                  selectedTokensOption?.tokens === option.tokens && styles.activeCard
                ]}
                onPress={() => handleSelectOption(option)}
              >
                <View style={styles.extraTokensCard}>
                  <View style={styles.tokensAndBadge}>
                    <Text
                      style={[
                        styles.planTitle,
                        selectedTokensOption?.tokens === option.tokens && styles.selectedText
                      ]}
                    >
                      {option.tokens} Tokens
                    </Text>
                      {option.label && (
                      <Text
                        style={[styles.badge, selectedTokensOption?.tokens === option.tokens && styles.activeBadge]}
                      >
                        {option.label}
                      </Text>
                    )}
                  </View>
                  <Text
                    style={[
                      styles.subscriptionPrice,
                      selectedTokensOption?.tokens === option.tokens && styles.selectedText
                    ]}
                  >
                    ${option.price.toFixed(2)}
                  </Text>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        )}
        </View>

        <View style={styles.buttonContainer}>
      <View style={styles.continueShadowBackground} />
      <TouchableOpacity
        // If the user has the plan, open cancel modal; otherwise continue purchase
        onPress={() => {
          const intent = userAlreadyHasThisPlan ? 'cancel' : 'purchase';
          if (parentalGatePassed) {
            if (intent === 'cancel') handleShowCancelModal();
            if (intent === 'purchase') handleContinueToPurchase();
          } else {
            setParentalGateIntent(intent);
            setShowParentalGate(true);
          }
        }}
        disabled={isContinueDisabled}
          style={[
            styles.continueButton,
            isContinueDisabled && { opacity: 0.5 }, // Dim the button if disabled
          ]}
      >
        <Text style={styles.continueButtonText}>
          {userAlreadyHasThisPlan ? 'Cancel Subscription' : 'Continue to Purchase'}
        </Text>
      </TouchableOpacity>
    </View>

    <Text style={{ textAlign: 'center', fontSize: 12 }}>
      By subscribing, you agree to our{' '}
      <Text
        style={{ color: '#007aff', textDecorationLine: 'underline' }}
        onPress={() => {
          if (parentalGatePassed) {
            Linking.openURL(PDF_URL);
          } else {
            setParentalGateIntent('terms');
            setShowParentalGate(true);
          }
        }}
      >
        Terms & Conditions
      </Text>{' '}
      and{' '}
      <Text
        style={{ color: '#007aff', textDecorationLine: 'underline' }}
        onPress={() => {
          if (parentalGatePassed) {
            Linking.openURL("https://legal.interactivebedtime.com/privacy_policy.html");
          } else {
            setParentalGateIntent('privacy');
            setShowParentalGate(true);
          }
        }}
      >
        Privacy Policy
      </Text>.
    </Text>


    

    {/* Show a simple cancel modal if showCancelModal === true */}
    {showCancelModal && (
      <View style={styles.modalBackdrop}>
        <View style={styles.modalContainer}>
          <Text style={styles.modalText}>
            Are you sure you want to cancel your subscription?
          </Text>
          <View style={styles.modalButtonRow}>
            <TouchableOpacity onPress={() => setShowCancelModal(false)}>
              <Text style={styles.modalCancel}>No, Keep It</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={handleConfirmCancel}>
              <Text style={styles.modalConfirm}>Yes, Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    )}
     {/* Subscription Warning Modal */}
     {showSubscriptionPopup && (
        <View style={styles.modalBackdrop}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>Active Subscription Detected</Text>
            <Text style={styles.modalText}>
              You already have an active subscription. Please cancel your current plan before selecting a new one.
            </Text>
            <TouchableOpacity
              style={styles.modalCloseButton}
              onPress={() => setShowSubscriptionPopup(false)}
            >
              <Text style={styles.modalCloseButtonText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    <ParentalGateModal
      isVisible={showParentalGate}
      onSuccess={handleParentalGateSuccess}
      onCancel={handleParentalGateCancel}
    />
    </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  subscriptionPage: {
    flexGrow: 1,
    backgroundColor: '#F7F8FB',
  },
  container: {
    flexGrow: 1, // Important: ensures content fills available height
    padding: 20,
    paddingVertical: 60, // Add extra padding to allow scrolling past the button
    alignItems: 'center',
    backgroundColor: '#F7F8FB',
  },  
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1,
    height: 60, // Pick a height that matches your design
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#F7F8FB',
    paddingHorizontal: 16,
  },
  backButton: {
    padding: 8,
  },
  backIcon: {
    width: 24,
    height: 24,
  },
  tokensButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  tokenIcon: {
    width: 24,
    height: 24,
  },
  tokenCount: {
    marginLeft: 5,
    fontSize: 16,
    color: '#534466',
    fontFamily: 'Futura',
  },
  adminButton: {
    backgroundColor: '#544564',
    padding: 10,
    borderRadius: 8,
  },
  adminButtonText: {
    color: '#fff',
    fontSize: 16,
  },
  title: {
    fontSize: 24,
    fontFamily: 'Futura',
    color: '#160014',
    marginVertical: 20,
  },
  subscriptionToggle: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
    marginBottom: 20,
    maxWidth: 600,
  },
  toggleButton: {
    padding: 10,
    borderRadius: 30,
    backgroundColor: '#d1d7e0',
  },
  activeButton: {
    backgroundColor: '#534466',
  },
  toggleButtonText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#160014',
  },
  activeButtonText: {
    color: '#fff', // Turn text white when selected
  },
  subscriptionCards: {
    width: '100%',
    maxWidth: 600,
  },
  subscriptionCard: {
    backgroundColor: '#D9D9D9',
    borderRadius: 15,
    padding: 20,
    marginVertical: 10,
    shadowColor: '#000',
    shadowOpacity: 0.1,
    shadowRadius: 6,
  },
  activeCard: {
    backgroundColor: '#544564',
  },
  cardContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  leftContent: {
    flex: 1,
  },
  titleRow: {
    flexDirection: 'row', // Ensure badge is aligned next to the title
    alignItems: 'center', // Vertically center the badge
  },
  extraTokensCard: {
    flexDirection: 'row',
    paddingVertical: 23,
    justifyContent: 'space-between', // Ensure price is on the right
  },
  tokensAndBadge: {
    flexDirection: 'row', // Align tokens and badge horizontally
    alignItems: 'center',
  },
  planTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    fontFamily: 'Futura',
  },
  badge: {
    backgroundColor: '#534466',
    color: '#fff',
    padding: 5,
    borderRadius: 15,
    fontSize: 12,
    marginLeft: 10, // Ensure badge is right next to the planTitle
  },
  activeBadge: {
    backgroundColor: '#fff',
    color: '#000',
  },
  subscriptionBenefit: {
    fontSize: 14,
    color: '#160014',
    marginTop: 5,
    fontFamily: 'Futura',
  },
  subscriptionTokens: {
    fontSize: 14,
    color: '#160014',
    marginTop: 5,
    fontFamily: 'Futura',
  },
  priceContainer: {
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  subscriptionPrice: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#000',
    fontFamily: 'Futura',
  },
  billingPeriod: {
    fontSize: 14,
    color: '#160014',
    fontFamily: 'Futura',
  },
  extraTokensSection: {
    backgroundColor: '#F7F8FB',
  },
  selectedText: {
    color: '#fff', // Turn text white when plan or tokens are selected
  },
  continueShadowBackground: {
    position: 'absolute',
    alignItems: 'center',
    width: 270,
    height: 70,
    backgroundColor: '#AFC9BF',
    borderRadius: 16,
    maxWidth: 400,
    top: '40%',
    left: '2%'
  },
  continueButton: {
    backgroundColor: '#544564',
    padding: 24,
    borderRadius: 16,
    marginTop: 32,
    width: 270,
    height: 70,
    alignItems: 'center',
    zIndex: 1,
    maxWidth: 400,
  },
  continueButtonText: {
    color: '#fff',
    fontSize: 18,
    fontFamily: 'Futura',
  },
  buttonContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  modalBackdrop: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(0,0,0,0.5)', // semi-transparent backdrop
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 999, // Ensure it layers above other content
  },
  // Container for the modal content
  modalContainer: {
    width: '80%',
    maxWidth: 400,
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
  },
  // Text inside the modal
  modalText: {
    fontSize: 16,
    color: '#000',
    textAlign: 'center',
    marginBottom: 20,
  },
  // Row holding the two button options
  modalButtonRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
  },
  // "No" or "Cancel" button text styling
  modalCancel: {
    fontSize: 16,
    color: '#534466',
    backgroundColor: '#D9D9D9',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    marginRight: 10,
  },
  // "Yes" or "Confirm" button text styling
  modalConfirm: {
    fontSize: 16,
    color: '#fff',
    backgroundColor: '#cd1c18',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    marginLeft: 10,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  modalCloseButton: {
    backgroundColor: '#007bff',
    padding: 10,
    borderRadius: 8,
  },
  modalCloseButtonText: {
    color: 'white',
    fontSize: 16,
  },
});

export default SubscriptionPage;
